import { useCallback, useRef, useState } from 'react';

import {
	AgentChatMessageFragment,
	AgentChatMessageRole,
	AgentChatToolCallFragment,
	FeatureName,
} from '@treams/treams-network';

import { useFeature } from './useFeature';
import { expectedUserVar } from './useSessionSlots';
import { useWorkspace } from './useWorkspace';

const GRAPHQL_ENDPOINT =
	import.meta.env.VITE_GRAPHQL_ENDPOINT ?? 'https://api.ontreams.com/graphql';
const AGENT_STREAM_URL = GRAPHQL_ENDPOINT.replace('/graphql', '/stream/v2/agent');
const ADVANCED_AGENT_STREAM_URL = GRAPHQL_ENDPOINT.replace('/graphql', '/stream/v2/advanced-agent');

export type AgentSSEEvent =
	| { type: 'token'; data: { text: string } }
	| {
			type: 'tool_call_start';
			data: { id: string; name: string; arguments: Record<string, unknown> };
	  }
	| {
			type: 'tool_call_end';
			data: { id: string; name: string; ok: boolean; preview?: string };
	  }
	| {
			type: 'message_complete';
			data: { chatID: string; messageId: string; finishReason: string };
	  }
	| { type: 'error'; data: { message: string } };

type StreamState = {
	messages: AgentChatMessageFragment[];
	activeAssistantIndex: number | null;
};

function parseSSEChunk(buffer: string): {
	events: AgentSSEEvent[];
	remaining: string;
} {
	const events: AgentSSEEvent[] = [];
	const parts = buffer.split('\n\n');
	const remaining = parts.pop() ?? '';

	for (const part of parts) {
		if (!part.trim()) {
			continue;
		}

		const lines = part.split('\n');
		let eventType = 'message';
		let data = '';

		for (const line of lines) {
			if (line.startsWith('event:')) {
				eventType = line.slice(6).trim();
			} else if (line.startsWith('data:')) {
				data = line.slice(5).trim();
			}
		}

		if (!data) {
			continue;
		}

		try {
			events.push({
				type: eventType as AgentSSEEvent['type'],
				data: JSON.parse(data),
			} as AgentSSEEvent);
		} catch {
			// Ignore malformed frames.
		}
	}

	return { events, remaining };
}

function createAssistantMessage(id: string, content = ''): AgentChatMessageFragment {
	return {
		id,
		role: AgentChatMessageRole.Assistant,
		content,
		createdAt: new Date(),
	};
}

function createToolMessage(toolCallId: string, content: string | null): AgentChatMessageFragment {
	return {
		id: `streaming-tool-${toolCallId}`,
		role: AgentChatMessageRole.Tool,
		content,
		toolCallId,
		createdAt: new Date(),
	};
}

function appendToken(
	state: StreamState,
	text: string,
	assistantSeq: { current: number },
): StreamState {
	const { messages, activeAssistantIndex } = state;

	if (activeAssistantIndex !== null) {
		const current = messages[activeAssistantIndex];
		const updated = [...messages];
		updated[activeAssistantIndex] = {
			...current,
			content: (current.content ?? '') + text,
		};
		return { ...state, messages: updated };
	}

	const id = `streaming-assistant-${assistantSeq.current++}`;
	return {
		messages: [...messages, createAssistantMessage(id, text)],
		activeAssistantIndex: messages.length,
	};
}

function appendToolCallStart(
	state: StreamState,
	toolCall: { id: string; name: string; arguments: Record<string, unknown> },
	assistantSeq: { current: number },
): StreamState {
	let { messages } = state;
	const { activeAssistantIndex } = state;
	let idx = activeAssistantIndex;

	if (idx === null) {
		const last = messages[messages.length - 1];
		if (last?.role === AgentChatMessageRole.Assistant && last.toolCalls?.length) {
			idx = messages.length - 1;
		} else {
			const id = `streaming-assistant-${assistantSeq.current++}`;
			messages = [...messages, createAssistantMessage(id)];
			idx = messages.length - 1;
		}
	}

	const current = messages[idx];
	const toolCalls: AgentChatToolCallFragment[] = [
		...(current.toolCalls ?? []),
		{
			id: toolCall.id,
			name: toolCall.name,
			arguments: toolCall.arguments,
		},
	];

	const updated = [...messages];
	updated[idx] = { ...current, toolCalls };

	const toolMessageExists = updated.some(
		(message) => message.role === AgentChatMessageRole.Tool && message.toolCallId === toolCall.id,
	);

	const nextMessages = toolMessageExists
		? updated
		: [...updated, createToolMessage(toolCall.id, null)];

	return {
		messages: nextMessages,
		activeAssistantIndex: null,
	};
}

function appendToolCallEnd(
	state: StreamState,
	toolCall: { id: string; preview?: string },
): StreamState {
	const existingIndex = state.messages.findIndex(
		(message) => message.role === AgentChatMessageRole.Tool && message.toolCallId === toolCall.id,
	);

	const content = toolCall.preview ?? '';

	if (existingIndex >= 0) {
		const updated = [...state.messages];
		updated[existingIndex] = {
			...updated[existingIndex],
			content,
		};
		return { ...state, messages: updated };
	}

	return {
		...state,
		messages: [...state.messages, createToolMessage(toolCall.id, content)],
	};
}

function applyEvent(
	state: StreamState,
	event: AgentSSEEvent,
	assistantSeq: { current: number },
): StreamState {
	switch (event.type) {
		case 'token':
			return appendToken(state, event.data.text, assistantSeq);
		case 'tool_call_start':
			return appendToolCallStart(state, event.data, assistantSeq);
		case 'tool_call_end':
			return appendToolCallEnd(state, event.data);
		default:
			return state;
	}
}

export function useAgent() {
	const workspace = useWorkspace();
	const hasAdvancedAgent = useFeature(FeatureName.AdvancedAgent);

	const [messages, setMessages] = useState<AgentChatMessageFragment[]>([]);
	const [chatID, setChatID] = useState<string | undefined>();
	const [isStreaming, setIsStreaming] = useState(false);
	const [error, setError] = useState<string | undefined>();

	const streamStateRef = useRef<StreamState>({ messages: [], activeAssistantIndex: null });
	const assistantSeqRef = useRef(0);
	const abortControllerRef = useRef<AbortController | null>(null);

	const reset = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		streamStateRef.current = { messages: [], activeAssistantIndex: null };
		assistantSeqRef.current = 0;
		setMessages([]);
		setChatID(undefined);
		setError(undefined);
		setIsStreaming(false);
	}, []);

	const send = useCallback(
		async (
			message: string,
			options: { chatID?: string; locale?: string; subjectID?: string; signal?: AbortSignal } = {},
		) => {
			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			if (options.signal) {
				if (options.signal.aborted) {
					controller.abort();
				} else {
					options.signal.addEventListener('abort', () => controller.abort(), { once: true });
				}
			}

			streamStateRef.current = { messages: [], activeAssistantIndex: null };
			assistantSeqRef.current = 0;
			setMessages([]);
			setError(undefined);
			setIsStreaming(true);

			// Authentication comes from the HttpOnly session cookie; the
			// X-Treams-User assertion makes a stale tab's stream fail fast
			// server-side instead of running as the wrong user.
			const expectedUserID = expectedUserVar();

			try {
				const streamUrl = hasAdvancedAgent ? ADVANCED_AGENT_STREAM_URL : AGENT_STREAM_URL;
				const response = await fetch(streamUrl, {
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'X-Treams-Workspace': workspace.id,
						...(expectedUserID ? { 'X-Treams-User': expectedUserID } : {}),
					},
					body: JSON.stringify({
						message,
						chatID: options.chatID,
						locale: options.locale,
						subjectID: options.subjectID,
					}),
					signal: controller.signal,
				});

				if (!response.ok || !response.body) {
					throw new Error(`Agent request failed with status ${response.status}`);
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = '';

				while (true) {
					const { done, value } = await reader.read();
					if (done || controller.signal.aborted) {
						break;
					}

					buffer += decoder.decode(value, { stream: true });
					const parsed = parseSSEChunk(buffer);
					buffer = parsed.remaining;

					for (const event of parsed.events) {
						if (controller.signal.aborted || abortControllerRef.current !== controller) {
							break;
						}
						switch (event.type) {
							case 'token':
							case 'tool_call_start':
							case 'tool_call_end':
								streamStateRef.current = applyEvent(streamStateRef.current, event, assistantSeqRef);
								setMessages(streamStateRef.current.messages);
								break;
							case 'message_complete':
								setChatID(event.data.chatID);
								break;
							case 'error':
								setError(event.data.message);
								throw new Error(event.data.message);
						}
					}
				}
			} catch (err) {
				if (
					controller.signal.aborted ||
					(err instanceof DOMException && err.name === 'AbortError')
				) {
					return;
				}
				const message = err instanceof Error ? err.message : 'An unexpected error occurred';
				setError(message);
				throw err;
			} finally {
				if (abortControllerRef.current === controller) {
					abortControllerRef.current = null;
					setIsStreaming(false);
				}
			}
		},
		[workspace.id, hasAdvancedAgent],
	);

	return {
		send,
		messages,
		chatID,
		isStreaming,
		error,
		reset,
	};
}
